import {
  compareNoteVersions,
  draftFromNote,
  isGoogleKeepRollbackTombstone,
  noteFromDraft,
  type ActiveDraft,
  type Note,
} from '../domain/note'

export function draftMatchesNote(draft: ActiveDraft, note: Note): boolean {
  return (
    draft.noteId === note.id
    && draft.body === note.body
    && draft.annotation === note.annotation
    && draft.expiresAt === note.expiresAt
    && draft.userTags.length === note.userTags.length
    && draft.userTags.every((tag, index) => tag === note.userTags[index])
    && draft.assetIds.length === note.assetIds.length
    && draft.assetIds.every((assetId, index) => assetId === note.assetIds[index])
  )
}

export function shouldPersistActiveDraft(
  draft: ActiveDraft,
  savedNote: Note | undefined,
): boolean {
  return !savedNote || !draftMatchesNote(draft, savedNote)
}

export function recoveryNoteForExplicitCapture(
  draft: ActiveDraft,
  savedNote: Note | undefined,
  deviceId: string,
  now = Date.now(),
): Note | null {
  if (!shouldPersistActiveDraft(draft, savedNote)) return null
  return noteFromDraft(draft, savedNote ?? null, deviceId, now)
}

export function synchronizedDraftReplacement(
  draft: ActiveDraft,
  previouslyRenderedNote: Note | null,
  synchronizedNote: Note,
  now = Date.now(),
): ActiveDraft | null {
  if (
    !previouslyRenderedNote
    || draft.noteId !== synchronizedNote.id
    || previouslyRenderedNote.id !== synchronizedNote.id
    || compareNoteVersions(synchronizedNote, previouslyRenderedNote) <= 0
    || !draftMatchesNote(draft, previouslyRenderedNote)
  ) return null

  return draftFromNote(synchronizedNote, now)
}

export function mergeSynchronizedNotes(notes: Note[], updates: Note[]): Note[] {
  if (updates.length === 0) return notes
  const byId = new Map(notes.map((note) => [note.id, note]))
  for (const update of updates) {
    const current = byId.get(update.id)
    if (isGoogleKeepRollbackTombstone(update)) {
      if (!current || compareNoteVersions(update, current) >= 0) byId.delete(update.id)
      continue
    }
    if (!current || compareNoteVersions(update, current) >= 0) byId.set(update.id, update)
  }
  return [...byId.values()].sort(
    (left, right) => right.modifiedAt - left.modifiedAt || right.id.localeCompare(left.id),
  )
}

export function acceptedSynchronizedNoteUpdates(
  currentNotes: ReadonlyMap<string, Note>,
  updates: Note[],
): Note[] {
  return updates.filter((update) => {
    const current = currentNotes.get(update.id)
    return !current || compareNoteVersions(update, current) >= 0
  })
}
